import { Link, useParams } from 'react-router-dom';
import { Chip } from '@/components/Chip/Chip';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { useCatalog } from '@/context/CatalogContext';
import { PRICE_BANDS, SORTS, useProductFilters } from '@/hooks/useProductFilters';
import { paths } from '@/routes/paths';
import styles from './Category.module.css';

export function Category() {
  const { categoryId = 'all' } = useParams();
  const { categories, status } = useCatalog();
  const filters = useProductFilters(categoryId);

  const title =
    categoryId === 'all'
      ? 'Everything'
      : (categories.find((c) => c.id === categoryId)?.name ?? 'Everything');

  return (
    <div className={styles.page}>
      <div className={styles.crumbs}>
        <Link to={paths.home}>Home</Link> / {title}
      </div>

      <div className={styles.head}>
        <h1 className={styles.title}>{title}</h1>
        <span className={styles.count}>{filters.filtered.length} items</span>
      </div>

      <div className={styles.filters}>
        {/* Row 1 — toggles. Quick, low-commitment narrowing. */}
        <div className={styles.chipRow}>
          {PRICE_BANDS.map((b) => (
            <Chip key={b.id} active={filters.band === b.id} onClick={() => filters.setBand(b.id)}>
              {b.label}
            </Chip>
          ))}

          {/* Only meaningful once something in the catalog is rated — books aren't. */}
          {filters.hasRatings && (
            <Chip active={filters.minRating} onClick={filters.toggleRating}>
              ★ 4.0+
            </Chip>
          )}

          <Chip active={filters.freeOnly} onClick={filters.toggleFree}>
            Free
          </Chip>

          <Chip active={filters.savedOnly} onClick={filters.toggleSaved}>
            ♡ Saved
          </Chip>
        </div>

        {/* Row 2 — book fields, in a grid so the labels and controls line up
            however many the catalog happens to offer. Sort sits at the end. */}
        <div className={styles.selectRow}>
          {filters.bookFilters.map((f) => (
            <div key={f.id} className={styles.field}>
              <label className={styles.fieldLabel} htmlFor={f.id}>
                {f.label}
              </label>
              <select
                id={f.id}
                className={styles.select}
                value={filters.values[f.id]}
                onChange={(e) => filters.setBookFilter(f.id, e.target.value)}
              >
                <option value="">Any</option>
                {f.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className={`${styles.field} ${styles.sortField}`}>
            <label className={styles.fieldLabel} htmlFor="sort">
              Sort
            </label>
            <select
              id="sort"
              className={styles.select}
              value={filters.sort}
              onChange={(e) => filters.setSort(e.target.value)}
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {filters.filtered.length === 0 ? (
        <div className={styles.emptyState}>
          {/* An empty grid means something different when the catalog never
              loaded — don't send people off adjusting filters that aren't the
              problem. CatalogStatus above explains the real cause. */}
          {status === 'error' ? (
            <div className={styles.emptyTitle}>No catalog to show</div>
          ) : status === 'loading' ? (
            <div className={styles.emptyTitle}>Loading…</div>
          ) : (
            <>
              <div className={styles.emptyTitle}>Nothing here (yet)</div>
              Try clearing a filter or two.
            </>
          )}
        </div>
      ) : (
        <div className={styles.grid}>
          {filters.filtered.map((p) => (
            <ProductCard key={p.id} product={p} showCategory />
          ))}
        </div>
      )}
    </div>
  );
}
